import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Download, Monitor, Apple, Shield, ArrowLeft, Smartphone, CheckCircle2, Loader2 } from 'lucide-react';
import { releaseInfo as fallbackReleaseInfo } from '../data/mock';

const ARTIFACTS_BASE = 'https://api.bubba.ai/api/v1/app-artifacts/loadsurf';

const DownloadPage = () => {
  const [platform, setPlatform] = useState('unknown');
  const [isMobile, setIsMobile] = useState(false);
  const [releaseInfo, setReleaseInfo] = useState(fallbackReleaseInfo);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch the latest build from the app artifacts API - same source the Bubba web app uses
  useEffect(() => {
    const fetchLatestRelease = async () => {
      try {
        const [mac, windows] = await Promise.all(
          ['mac', 'win'].map(async (artifactPlatform) => {
            const response = await fetch(`${ARTIFACTS_BASE}/${artifactPlatform}/latest`);

            if (!response.ok) {
              throw new Error(`Failed to fetch ${artifactPlatform} release`);
            }

            return response.json();
          })
        );

        setReleaseInfo({
          version: mac.version,
          downloads: {
            // Link to the stable file path, which signs a fresh URL on every request
            mac: {
              url: `${ARTIFACTS_BASE}/mac/${mac.file_name}`,
              fileName: mac.file_name,
            },
            windows: {
              url: `${ARTIFACTS_BASE}/win/${windows.file_name}`,
              fileName: windows.file_name,
            },
          },
        });
      } catch (error) {
        console.error('Failed to fetch latest release, using fallback:', error);
        // Keep the fallback release info (already set as initial state)
      } finally {
        setIsLoading(false);
      }
    };

    fetchLatestRelease();
  }, []);

  useEffect(() => {
    const detectPlatform = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const platform = navigator.platform.toLowerCase();
      
      // Check for mobile
      if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
        setIsMobile(true);
        return 'mobile';
      }
      
      // Check for macOS
      if (platform.includes('mac') || userAgent.includes('macintosh') || userAgent.includes('mac os')) {
        return 'mac';
      }
      
      // Default to Windows
      return 'windows';
    };

    setPlatform(detectPlatform());
  }, []);

  const handleDownload = (downloadPlatform) => {
    const downloadUrl = releaseInfo.downloads[downloadPlatform]?.url;
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  if (isMobile) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          {/* Logo */}
          <a href="/" className="inline-flex items-center gap-3 mb-8">
            <img 
              src="/logo/Load Surf SVG.svg" 
              alt="LoadSurf AI" 
              className="h-12 w-12"
            />
            <span className="text-2xl font-bold text-white">
              LoadSurf<span className="text-amber-400">.AI</span>
            </span>
          </a>

          <div className="bg-slate-900/70 border border-slate-800 rounded-3xl p-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-sky-500/20 to-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <Smartphone className="w-10 h-10 text-amber-400" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-4">
              Desktop App Required
            </h1>
            
            <p className="text-slate-400 mb-6">
              LoadSurf AI is a desktop application for Windows and macOS. 
              Please visit this page on your computer to download.
            </p>

            <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
              <p className="text-sm text-slate-300 font-mono break-all">
                loadsurf.ai/download
              </p>
            </div>

            <p className="text-sm text-slate-500">
              Mobile apps coming soon!
            </p>
          </div>

          <a 
            href="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mt-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <a href="/" className="inline-flex items-center gap-3 mb-8">
            <img 
              src="/logo/Load Surf SVG.svg" 
              alt="LoadSurf AI" 
              className="h-12 w-12"
            />
            <span className="text-2xl font-bold text-white">
              LoadSurf<span className="text-amber-400">.AI</span>
            </span>
          </a>

          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Download LoadSurf AI
          </h1>
          <p className="text-lg text-slate-400">
            {isLoading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading latest version...
              </span>
            ) : (
              `Version ${releaseInfo.version} • Search all your loadboards in one tab`
            )}
          </p>
        </div>

        {/* Download Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Windows Card */}
          <div className={`bg-slate-900/70 border rounded-3xl p-8 transition-all ${
            platform === 'windows' 
              ? 'border-sky-500/50 ring-2 ring-sky-500/20' 
              : 'border-slate-800 hover:border-slate-700'
          }`}>
            {platform === 'windows' && (
              <div className="inline-block px-3 py-1 bg-sky-500/10 text-sky-400 text-xs font-semibold rounded-full mb-4">
                RECOMMENDED FOR YOU
              </div>
            )}
            
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500/20 to-sky-600/20 flex items-center justify-center">
                <Monitor className="w-8 h-8 text-sky-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Windows</h2>
                <p className="text-slate-400 text-sm">Windows 10 or later (64-bit)</p>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-slate-400 mb-6">
              <span>{releaseInfo.downloads.windows.fileName}</span>
            </div>

            <Button
              size="lg"
              className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold py-6 text-lg"
              onClick={() => handleDownload('windows')}
            >
              <Download className="mr-2 w-5 h-5" />
              Download for Windows
            </Button>
          </div>

          {/* macOS Card */}
          <div className={`bg-slate-900/70 border rounded-3xl p-8 transition-all ${
            platform === 'mac' 
              ? 'border-amber-500/50 ring-2 ring-amber-500/20' 
              : 'border-slate-800 hover:border-slate-700'
          }`}>
            {platform === 'mac' && (
              <div className="inline-block px-3 py-1 bg-amber-500/10 text-amber-400 text-xs font-semibold rounded-full mb-4">
                RECOMMENDED FOR YOU
              </div>
            )}
            
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 flex items-center justify-center">
                <Apple className="w-8 h-8 text-amber-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">macOS</h2>
                <p className="text-slate-400 text-sm">macOS 11+ (Apple Silicon & Intel)</p>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-slate-400 mb-6">
              <span>{releaseInfo.downloads.mac.fileName}</span>
            </div>

            <Button
              size="lg"
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-6 text-lg"
              onClick={() => handleDownload('mac')}
            >
              <Download className="mr-2 w-5 h-5" />
              Download for Mac
            </Button>
          </div>
        </div>

        {/* Security Note */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Your credentials stay on your machine</h3>
              <p className="text-slate-400 text-sm">
                LoadSurf AI stores all your loadboard credentials locally using AES-256 encryption. 
                We never see or store your passwords on any server.
              </p>
            </div>
          </div>
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span>Virus scanned</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span>Code signed</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span>Auto-updates</span>
          </div>
        </div>

        {/* Footer Links */}
        <div className="text-center">
          <a 
            href="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </a>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
